import { useEffect, useCallback, useMemo, useState } from "react";

import app from "@Stores/app";
import createStore from "@Stores/base-factories";
import hookSiteSelectedFactory from "@Stores/base-factories/store-site-selection"
import { useGlobalStore } from "@Stores/global";

const useThemeFactory = ({ fetchCms, siteSelected: { id: siteId, key: siteKey } }) => {
  const [themes, setThemes] = useState({})

  const list = useCallback(async () => {
    const themes = await fetchCms({
      method: 'get',
      url: 'themes',
      params: { [siteKey]: siteId },
    })
    setThemes(themes)
  }, [fetchCms, siteId, siteKey])

  return useMemo(() => ({
    themes,

    useLoad: () => {
      useEffect(() => {
        list()
      }, [])
    },
  }), [themes, list])
}

const { Provider: ThemeProvider, useStore: useThemeStore } = createStore()
  .extend(hookSiteSelectedFactory('site'))
  .extend(useThemeFactory)
  .factory()

const hookSettingFactory = collectionURL => ({ fetch, siteSelected: { id: siteId, key: siteKey } }) => {
  const { siteSettings: { fetchAndUpdate } } = useGlobalStore()
  const [settings, setSettings] = useState({})

  const fetchGlobal = useCallback(async () => {
    const settings = await fetchAndUpdate()
    setSettings(settings)
    return settings
  }, [fetchAndUpdate])

  const save = useCallback(async payload => {
    const settings = await fetch({
      method: 'put',
      url: `${collectionURL}/${siteId}`,
      data: payload,
    })
    fetchAndUpdate()
    return settings
  }, [fetch, siteId, fetchAndUpdate])

  const syncTheme = useCallback(async () => {
    await fetch({
      method: 'post',
      url: `${collectionURL}/syncTheme`,
      data: { [siteKey]: siteId },
    })
  }, [fetch, siteId, siteKey])

  const fetchSiteSettings = useCallback(async () => await fetch({
    method: 'get',
    url: `site/${siteId}`,
  }), [fetch, siteId])

  const updateDomains = useCallback(async payload => {
    await fetch({
      method: 'put',
      url: `site/${siteId}`,
      data: { domain_aliases: payload.system_config.wildcard.wildcard },
    })
    app.sites = []
    await app.setSiteById(siteId)
  }, [fetch, siteId])

  return useMemo(() => ({
    syncTheme,
    settings,
    fetchSiteSettings,

    useLoad: () => {
      useEffect(() => {
        fetchGlobal()
      }, [])
    },

    useSubmit: () => useCallback(async payload => {
      setSettings(payload)
      if (payload.system_config?.wildcard) {
        await updateDomains(payload)
        delete payload.system_config.wildcard
      }
      const data = await save(payload)
      await fetchGlobal()
      return data
    }, []),
  }), [syncTheme, settings, fetchGlobal, save, updateDomains, fetchSiteSettings])
}

const { Provider: SettingProvider, useStore: useSettingStore } = createStore()
  .extend(hookSiteSelectedFactory('site_id'))
  .extend(hookSettingFactory('cms/setting'))
  .factory()

export {
  ThemeProvider,
  useThemeStore,
  SettingProvider,
  useSettingStore,
}
